import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { inferenceAPI } from '../lib/api';
import ComputeTab from '../components/inference/ComputeTab';
import {
  Server, Cpu, FileText, Terminal, Plus, Trash2, Loader2, Check, Copy, ArrowLeft,
  AlertTriangle, Eye, EyeOff, Zap, History, RotateCcw, Edit3, XCircle,
} from 'lucide-react';

const PARENT_TABS = [
  { id: 'endpoints',      label: 'Endpoints',      icon: Server },
  { id: 'models',         label: 'Models',         icon: Cpu },
  { id: 'compute',        label: 'Compute',        icon: Zap },
  { id: 'prompts',        label: 'Prompts',        icon: FileText },
  { id: 'infrastructure', label: 'Infrastructure', icon: Terminal },
];

const SUBTABS = {
  endpoints:      ['all', 'ollama', 'openai'],
  models:         ['all', 'ollama', 'openai'],
  compute:        ['windows', 'queue', 'settings'],
  prompts:        ['system', 'retry_suffix', 'user', 'image'],
  infrastructure: [],
};

const SUBTAB_LABELS = {
  all: 'All', ollama: 'Ollama', openai: 'Azure OpenAI',
  windows: 'Windows', queue: 'Queue', settings: 'Automation',
  system: 'Instructions', retry_suffix: 'Retry addendum', user: 'Story prompt', image: 'Image prompt',
};

const PROMPT_HELP = {
  system: 'House style, accuracy rules and the output contract. Sent with every request. Changing this affects every story generated across the network.',
  retry_suffix: 'Appended to the instructions when a first attempt fails validation. Keep it short and corrective.',
  user: 'The per-story layer — source text, allowed tags and house style notes. Rarely needs changing.',
};

const inputCls = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const fmtSize = (bytes) => (bytes ? `${(bytes / 1e9).toFixed(1)} GB` : '—');


function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>}
    </div>
  );
}

function HealthBadge({ status }) {
  const styles = {
    healthy: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed:  'bg-red-50 text-red-600 border-red-200',
    unknown: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border font-semibold ${styles[status] || styles.unknown}`}>
      {status === 'healthy' ? 'Healthy' : status === 'failed' ? 'Failed' : 'Not tested'}
    </span>
  );
}

function BackLink({ label, onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors">
      <ArrowLeft size={14} /> {label}
    </button>
  );
}


// ─── Endpoint form ───

function EndpointForm({ endpoint, onSaved, onCancel }) {
  const isNew = !endpoint;
  const [form, setForm] = useState({
    name:            endpoint?.name || '',
    provider:        endpoint?.provider || 'ollama',
    base_url:        endpoint?.base_url || '',
    auth_token:      '',
    timeout_seconds: endpoint?.timeout_seconds ?? 120,
    is_active:       endpoint?.is_active ?? true,
    notes:           endpoint?.notes || '',
  });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe]     = useState(null);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleProbe = async () => {
    if (!form.base_url.trim()) return toast.error('Base URL is required');
    setProbing(true);
    setProbe(null);
    try {
      const { data } = await inferenceAPI.probeEndpoint({
        base_url: form.base_url.trim(),
        auth_token: form.auth_token,
      });
      setProbe(data);
      if (data.ok) toast.success(`Reachable — ${data.models?.length || 0} model(s) installed`);
      else toast.error(data.error || 'Not reachable');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setProbing(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim())     return toast.error('Name is required');
    if (!form.base_url.trim()) return toast.error('Base URL is required');

    setSaving(true);
    try {
      const payload = {
        name:            form.name.trim(),
        base_url:        form.base_url.trim(),
        timeout_seconds: parseInt(form.timeout_seconds, 10) || 120,
        is_active:       form.is_active,
        notes:           form.notes.trim() || null,
      };
      if (form.auth_token) payload.auth_token = form.auth_token;
      if (isNew) payload.provider = form.provider;

      if (isNew) {
        await inferenceAPI.createEndpoint(payload);
        toast.success('Endpoint created');
      } else {
        await inferenceAPI.updateEndpoint(endpoint.id, payload);
        toast.success('Endpoint updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <BackLink label="Back to Endpoints" onClick={onCancel} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{isNew ? 'Add Endpoint' : 'Edit Endpoint'}</h1>
        <p className="text-slate-500 text-sm mt-1">
          Where inference requests are sent. Swapping GPU means changing the URL here, with no deploy.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="space-y-5 max-w-2xl">
          <Field label="Name" required>
            <input value={form.name} onChange={e => update('name', e.target.value)}
              placeholder="e.g. Vast RTX 3090" className={inputCls} />
          </Field>

          <Field label="Provider" required hint={isNew ? null : 'Provider cannot be changed after creation.'}>
            <select value={form.provider} onChange={e => update('provider', e.target.value)}
              disabled={!isNew} className={`${inputCls} ${!isNew ? 'bg-slate-50 text-slate-400' : ''}`}>
              <option value="ollama">Ollama</option>
              <option value="openai">Azure OpenAI</option>
            </select>
          </Field>

          <Field label="Base URL" required hint="No trailing slash and no /api — the client appends the path itself.">
            <input value={form.base_url} onChange={e => update('base_url', e.target.value)}
              placeholder="https://gpu.okebourne.com" className={`${inputCls} font-mono text-xs`} />
          </Field>

          <Field label="Auth token"
            hint="Encrypted before storage and never readable again. The token only — the client adds the Bearer prefix.">
            <div className="relative">
              <input type={showToken ? 'text' : 'password'} value={form.auth_token}
                onChange={e => update('auth_token', e.target.value)} autoComplete="off"
                placeholder={!isNew && endpoint?.credential?.is_set ? `${endpoint.credential.masked} — leave blank to keep` : 'Bearer token'}
                className={`${inputCls} font-mono text-xs pr-10`} />
              <button type="button" onClick={() => setShowToken(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {showToken ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>

          <Field label="Timeout (seconds)" hint="Generation typically takes under 15 seconds. Raise this if long sources time out.">
            <input type="number" min="10" value={form.timeout_seconds}
              onChange={e => update('timeout_seconds', e.target.value)} className={`${inputCls} max-w-[140px]`} />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active}
              onChange={e => update('is_active', e.target.checked)} className="h-4 w-4 accent-blue-600" />
            Use this endpoint for {form.provider === 'openai' ? 'Azure OpenAI' : 'Ollama'} requests
          </label>

          <Field label="Notes">
            <input value={form.notes} onChange={e => update('notes', e.target.value)}
              placeholder="Optional — instance ID, ports, anything worth remembering" className={inputCls} />
          </Field>

          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={handleProbe} disabled={probing}
                className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                {probing ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} Test connection
              </button>
              {probe && !probe.ok && <span className="text-sm text-red-600">{probe.error}</span>}
            </div>

            {probe?.ok && (
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-xs text-slate-500 mb-2">{probe.models?.length || 0} model(s) installed</p>
                <div className="space-y-1">
                  {probe.models?.map(m => (
                    <div key={m.name} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-slate-900">{m.name}</span>
                      <span className="text-slate-500">{m.parameters} · {m.quantisation} · {fmtSize(m.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {isNew ? 'Create endpoint' : 'Save changes'}
            </button>
            <button onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Endpoints list ───

function EndpointRow({ endpoint, onClick, onTest, testing, onDelete }) {
  return (
    <div className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-300 transition-colors">
      <div className="flex items-center gap-4">
        <button onClick={onClick} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Server size={15} className="text-slate-400" />
            <span className="font-semibold text-slate-900">{endpoint.name}</span>
            <HealthBadge status={endpoint.health_status} />
            {endpoint.is_active && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 font-semibold">
                Active
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 font-mono truncate">{endpoint.base_url}</p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 flex-wrap">
            <span>{endpoint.provider === 'openai' ? 'Azure OpenAI' : 'Ollama'}</span>
            <span>{endpoint.credential?.is_set ? `Token ${endpoint.credential.masked}` : 'No token set'}</span>
            <span>Timeout {endpoint.timeout_seconds}s</span>
            {endpoint.last_checked_at && <span>Checked {fmtUK(endpoint.last_checked_at)}</span>}
          </div>
          {endpoint.last_error && <p className="text-xs text-red-500 mt-1 truncate">{endpoint.last_error}</p>}
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onTest} disabled={testing} title="Test connection"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40">
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
          </button>
          <button onClick={onClick} title="Edit"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
            <Edit3 size={15} />
          </button>
          <button onClick={onDelete} title="Remove"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}


function EndpointsTab({ filter }) {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [testingId, setTestingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await inferenceAPI.getEndpoints();
      setEndpoints(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const { data } = await inferenceAPI.testEndpoint(id);
      if (data.ok) toast.success(`Reachable — ${data.models?.length || 0} model(s) installed`);
      else toast.error(data.error || 'Not reachable');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (ep) => {
    if (!window.confirm(`Remove "${ep.name}"?\n\nIts stored auth token is deleted too. Models using it must be reassigned first.`)) return;
    try {
      await inferenceAPI.deleteEndpoint(ep.id);
      toast.success('Endpoint removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (editing) {
    return (
      <EndpointForm
        endpoint={editing.id ? editing : null}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const visible = filter === 'all' ? endpoints : endpoints.filter(e => e.provider === filter);

  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <p className="text-xs text-slate-400">
          {loading ? 'Loading…' : `${visible.length} endpoint${visible.length === 1 ? '' : 's'}`}
        </p>
        <button onClick={() => setEditing({})}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={15} /> Add Endpoint
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading endpoints...
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          No endpoints yet. Add one to point Okebourne at your inference host.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(ep => (
            <EndpointRow key={ep.id} endpoint={ep}
              onClick={() => setEditing(ep)}
              onTest={() => handleTest(ep.id)}
              testing={testingId === ep.id}
              onDelete={() => handleDelete(ep)} />
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Model form ───

function ModelForm({ model, endpoints, installed, onSaved, onCancel }) {
  const isNew = !model;
  const [form, setForm] = useState({
    label:             model?.label || '',
    provider:          model?.provider || 'ollama',
    model_id:          model?.model_id || '',
    model_tag:         model?.model_tag || '',
    model_digest:      model?.model_digest || '',
    endpoint_id:       model?.endpoint_id || '',
    supports_thinking: model?.supports_thinking ?? false,
    disable_thinking:  model?.disable_thinking ?? true,
    temperature:       model?.temperature ?? 0.3,
    max_tokens:        model?.max_tokens ?? 3000,
    description:       model?.description || '',
    is_active:         model?.is_active ?? true,
    is_default:        model?.is_default ?? false,
  });
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const pickInstalled = (name) => {
    const found = installed.find(m => m.name === name);
    if (!found) return;
    setForm(prev => ({
      ...prev,
      model_tag:         found.name,
      model_digest:      found.digest || '',
      supports_thinking: (found.capabilities || []).includes('thinking'),
      label:             prev.label || found.name,
    }));
  };

  const handleSubmit = async () => {
    if (!form.label.trim())     return toast.error('Label is required');
    if (!form.model_tag.trim()) return toast.error('Model tag is required');

    setSaving(true);
    try {
      const payload = {
        label:             form.label.trim(),
        model_tag:         form.model_tag.trim(),
        model_digest:      form.model_digest.trim() || null,
        endpoint_id:       form.endpoint_id || null,
        supports_thinking: form.supports_thinking,
        disable_thinking:  form.disable_thinking,
        temperature:       parseFloat(form.temperature),
        max_tokens:        parseInt(form.max_tokens, 10),
        description:       form.description.trim(),
        is_active:         form.is_active,
        is_default:        form.is_default,
      };
      if (isNew) {
        payload.provider = form.provider;
        if (form.model_id.trim()) payload.model_id = form.model_id.trim();
      }

      if (isNew) {
        await inferenceAPI.createModel(payload);
        toast.success('Model added');
      } else {
        await inferenceAPI.updateModel(model.id, payload);
        toast.success('Model updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <BackLink label="Back to Models" onClick={onCancel} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{isNew ? 'Add Model' : 'Edit Model'}</h1>
        <p className="text-slate-500 text-sm mt-1">
          Models available to the rewriter. Adding one here makes it selectable without a code change.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="space-y-5 max-w-2xl">
          {isNew && installed.length > 0 && (
            <Field label="Installed on the endpoint"
              hint="Picking one fills the tag, digest and reasoning capability below.">
              <select value="" onChange={e => pickInstalled(e.target.value)} className={inputCls}>
                <option value="">Select a model…</option>
                {installed.map(m => (
                  <option key={m.name} value={m.name}>
                    {m.name} — {m.parameters}, {m.quantisation}, {fmtSize(m.size)}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Label" required>
            <input value={form.label} onChange={e => update('label', e.target.value)}
              placeholder="e.g. Qwen 3.5 9B" className={inputCls} />
          </Field>

          <Field label="Provider" required hint={isNew ? null : 'Provider cannot be changed after creation.'}>
            <select value={form.provider} onChange={e => update('provider', e.target.value)}
              disabled={!isNew} className={`${inputCls} ${!isNew ? 'bg-slate-50 text-slate-400' : ''}`}>
              <option value="ollama">Ollama</option>
              <option value="openai">Azure OpenAI</option>
            </select>
          </Field>

          <Field label="Model tag" required>
            <input value={form.model_tag} onChange={e => update('model_tag', e.target.value)}
              placeholder="qwen3.5:9b" className={`${inputCls} font-mono text-xs`} />
          </Field>

          <Field label="Digest" hint="Pins the exact weights. Tags can be updated upstream without warning.">
            <input value={form.model_digest} onChange={e => update('model_digest', e.target.value)}
              placeholder="Optional" className={`${inputCls} font-mono text-xs`} />
          </Field>

          {isNew ? (
            <Field label="Model ID"
              hint="Recorded against every story this model generates. Leave blank to derive it from the tag. Set it explicitly to register the same tag twice with different settings.">
              <input value={form.model_id} onChange={e => update('model_id', e.target.value)}
                placeholder={form.model_tag ? `${form.provider}:${form.model_tag}` : 'provider:tag'}
                className={`${inputCls} font-mono text-xs`} />
            </Field>
          ) : (
            <Field label="Model ID" hint="Cannot be changed — historical stories reference it.">
              <input value={model.model_id} disabled
                className={`${inputCls} font-mono text-xs bg-slate-50 text-slate-400`} />
            </Field>
          )}

          <Field label="Endpoint" hint="Leave on the default to follow whichever endpoint is active.">
            <select value={form.endpoint_id} onChange={e => update('endpoint_id', e.target.value)} className={inputCls}>
              <option value="">Use the active endpoint</option>
              {endpoints.filter(e => e.provider === form.provider).map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-4 max-w-md">
            <Field label="Temperature">
              <input type="number" step="0.05" min="0" max="2" value={form.temperature}
                onChange={e => update('temperature', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Max tokens">
              <input type="number" min="100" value={form.max_tokens}
                onChange={e => update('max_tokens', e.target.value)} className={inputCls} />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.supports_thinking}
              onChange={e => update('supports_thinking', e.target.checked)} className="h-4 w-4 accent-blue-600" />
            This is a reasoning model
          </label>

          {form.supports_thinking && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <label className="flex items-center gap-2 text-sm text-amber-900">
                <input type="checkbox" checked={!form.disable_thinking}
                  onChange={e => update('disable_thinking', !e.target.checked)} className="h-4 w-4 accent-amber-600" />
                Enable reasoning (experimental)
              </label>
              <p className="text-xs text-amber-700 mt-1.5">
                Reasoning output can break JSON parsing and adds significant generation time. Leave off unless testing.
              </p>
            </div>
          )}

          <Field label="Description">
            <input value={form.description} onChange={e => update('description', e.target.value)}
              placeholder="Optional" className={inputCls} />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active}
              onChange={e => update('is_active', e.target.checked)} className="h-4 w-4 accent-blue-600" />
            Active — available in the Generate dropdown
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_default}
              onChange={e => update('is_default', e.target.checked)} className="h-4 w-4 accent-blue-600" />
            Use as the default model
          </label>

          <div className="flex items-center gap-2 pt-2">
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {isNew ? 'Add model' : 'Save changes'}
            </button>
            <button onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Models list ───

function ModelRow({ model, onClick, onDelete }) {
  return (
    <div className={`w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-300 transition-colors ${model.is_active === false ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-4">
        <button onClick={onClick} disabled={!model.id} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Cpu size={15} className="text-slate-400" />
            <span className="font-semibold text-slate-900">{model.label}</span>
            {model.is_default && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 font-semibold">
                Default
              </span>
            )}
            {model.installed === false && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-red-50 text-red-600 rounded border border-red-200 font-semibold">
                Not on endpoint
              </span>
            )}
            {model.is_active === false && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-slate-100 text-slate-500 rounded border border-slate-200 font-semibold">
                Inactive
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 font-mono truncate">
            {model.model_id || model.model_tag}
            {model.model_digest && <span className="text-slate-300"> · {String(model.model_digest).slice(0, 12)}</span>}
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 flex-wrap">
            <span>{model.provider === 'openai' ? 'Azure OpenAI' : 'Ollama'}</span>
            <span>Temp {model.temperature}</span>
            <span>Max {model.max_tokens} tokens</span>
            {model.supports_thinking && (
              <span className={model.disable_thinking ? '' : 'text-amber-600'}>
                Reasoning {model.disable_thinking ? 'suppressed' : 'enabled'}
              </span>
            )}
          </div>
          {model.description && <p className="text-xs text-slate-400 mt-1">{model.description}</p>}
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onClick} disabled={!model.id} title="Edit"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30">
            <Edit3 size={15} />
          </button>
          <button onClick={onDelete} disabled={!model.id} title="Deactivate"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}


function ModelsTab({ filter }) {
  const [data, setData]           = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, e] = await Promise.all([inferenceAPI.getModels(), inferenceAPI.getEndpoints()]);
      setData(m.data);
      setEndpoints(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load models');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove "${m.label}"?\n\nIf any stories were generated with it, you'll be offered deactivation instead.`)) return;

    try {
      await inferenceAPI.permanentDeleteModel(m.id);
      toast.success('Model deleted');
      load();
      return;
    } catch (err) {
      if (err.response?.status !== 409) {
        toast.error(err.response?.data?.detail || 'Failed to delete');
        return;
      }
      const detail = err.response.data.detail;
      if (!window.confirm(`${detail}\n\nDeactivate it instead?`)) return;
    }

    try {
      await inferenceAPI.deleteModel(m.id);
      toast.success('Model deactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const installed = data?.installed_on_endpoint || [];

  if (editing) {
    return (
      <ModelForm
        model={editing.id ? editing : null}
        endpoints={endpoints}
        installed={installed}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const models  = data?.models || [];
  const visible = filter === 'all' ? models : models.filter(m => m.provider === filter);

  return (
    <div>
      {!loading && data && !data.endpoint_ok && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 mb-4 text-sm text-red-700">
          <XCircle size={15} className="shrink-0 mt-0.5" />
          <span>The active endpoint is not reachable, so installed models could not be checked.</span>
        </div>
      )}

      <div className="flex items-end justify-between mb-4">
        <p className="text-xs text-slate-400">
          {loading ? 'Loading…' : `${visible.length} model${visible.length === 1 ? '' : 's'}`}
        </p>
        <button onClick={() => setEditing({})}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={15} /> Add Model
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading models...
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          No models yet. Add one so the rewriter can select it.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(m => (
            <ModelRow key={m.id || m.model_id} model={m}
              onClick={() => setEditing(m)}
              onDelete={() => handleDelete(m)} />
          ))}
        </div>
      )}

      {installed.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mt-5">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">
            Installed on the active endpoint
          </p>
          <div className="space-y-1">
            {installed.map(m => (
              <div key={m.name} className="flex items-center justify-between text-xs">
                <span className="font-mono text-slate-900">{m.name}</span>
                <span className="text-slate-500">
                  {m.parameters} · {m.quantisation} · {fmtSize(m.size)}
                  {(m.capabilities || []).includes('thinking') && <span className="ml-2 text-amber-600">reasoning</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Prompts ───

function PromptsTab({ layer }) {
  const [active, setActive]     = useState(null);
  const [body, setBody]         = useState('');
  const [original, setOriginal] = useState('');
  const [versions, setVersions] = useState([]);
  const [notes, setNotes]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([
        inferenceAPI.getActivePrompt(layer),
        inferenceAPI.getPrompts(layer),
      ]);
      setActive(a.data);
      setBody(a.data.body || '');
      setOriginal(a.data.body || '');
      setVersions(v.data.versions || []);
      setNotes('');
      setShowHistory(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load prompt');
    } finally {
      setLoading(false);
    }
  }, [layer]);

  useEffect(() => { load(); }, [load]);

  const dirty = body !== original;
  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;

  const handleSave = async () => {
    if (!body.trim()) return toast.error('Prompt cannot be empty');
    setSaving(true);
    try {
      await inferenceAPI.savePrompt({ layer, body, notes: notes.trim() || null, activate: true });
      toast.success('Prompt saved and made active');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id, version) => {
    if (!window.confirm(`Activate version ${version}?\n\nThis takes effect immediately for all new stories.`)) return;
    try {
      await inferenceAPI.activatePrompt(id);
      toast.success(`Version ${version} activated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading prompt...
      </div>
    );
  }

  if (showHistory) {
    return (
      <div>
        <BackLink label="Back to editor" onClick={() => setShowHistory(false)} />
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Version history</h1>
          <p className="text-slate-500 text-sm mt-1">
            {SUBTAB_LABELS[layer]} — activating an earlier version takes effect immediately.
          </p>
        </div>
        <div className="space-y-2">
          {versions.map(v => (
            <div key={v.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-slate-900">Version {v.version}</span>
                  {v.is_active && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 font-semibold">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  {fmtUK(v.created_at)} · {v.body_length?.toLocaleString()} characters
                </p>
                {v.notes && <p className="text-xs text-slate-500 mt-1">{v.notes}</p>}
              </div>
              {!v.is_active && (
                <button onClick={() => handleActivate(v.id, v.version)}
                  className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0">
                  <RotateCcw size={14} /> Activate
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">{PROMPT_HELP[layer]}</p>
        {versions.length > 0 && (
          <button onClick={() => setShowHistory(true)}
            className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0">
            <History size={14} /> History
            <span className="text-xs text-slate-400">{versions.length}</span>
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
          <span>{wordCount.toLocaleString()} words</span>
          <span>{body.length.toLocaleString()} characters</span>
          {active?.version > 0 && <span>Version {active.version}</span>}
          {dirty && <span className="text-amber-600 font-medium">Unsaved changes</span>}
        </div>

        <textarea value={body} onChange={e => setBody(e.target.value)} spellCheck={false}
          className="w-full h-[520px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 font-mono leading-relaxed focus:outline-none focus:border-blue-500 resize-y" />

        {dirty && (
          <div className="mt-4">
            <Field label="What changed">
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Optional — e.g. tightened the headline rules" className={inputCls} />
            </Field>
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          <button onClick={handleSave} disabled={saving || !dirty || !body.trim()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {saving && <Loader2 size={15} className="animate-spin" />}
            Save new version
          </button>
          {dirty && (
            <button onClick={() => { setBody(original); setNotes(''); }}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Discard changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



function ImagePromptTab() {
  const [body, setBody]         = useState('');
  const [original, setOriginal] = useState('');
  const [source, setSource]     = useState('code');
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await inferenceAPI.getImagePrompt();
      setBody(data.body || '');
      setOriginal(data.body || '');
      setSource(data.source || 'code');
      setPlaceholders(data.placeholders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load the image prompt');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = body !== original;

  const handleSave = async () => {
    if (!body.trim()) return toast.error('Prompt cannot be empty');
    setSaving(true);
    try {
      const { data } = await inferenceAPI.saveImagePrompt({ body });
      setBody(data.body || '');
      setOriginal(data.body || '');
      setSource(data.source || 'database');
      toast.success('Image prompt saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading image prompt...
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-slate-500 max-w-2xl mb-4">
        {source === 'database'
          ? 'This saved template is used for every AI image generated, in Okebourne and Tempust alike.'
          : 'Showing the built-in template. Edit and save to take over from it.'}
      </p>

      {placeholders.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
          <p className="text-xs text-slate-500 mb-2">
            Placeholders below are replaced with story content.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map(p => (
              <span key={p}
                className="text-xs px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600 font-mono">
                {`{{${p}}}`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
          {dirty && <span className="text-amber-600 font-medium">Unsaved changes</span>}
        </div>

        <textarea value={body} onChange={e => setBody(e.target.value)} spellCheck={false}
          className="w-full h-[520px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 font-mono leading-relaxed focus:outline-none focus:border-blue-500 resize-y" />

        <p className="text-xs text-slate-400 mt-2">
          Keep the finished prompt under roughly 600 words. 
        </p>

        <div className="flex items-center gap-2 mt-4">
          <button onClick={handleSave} disabled={saving || !dirty || !body.trim()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
            {saving && <Loader2 size={15} className="animate-spin" />}
            Save prompt
          </button>
          {dirty && (
            <button onClick={() => setBody(original)}
              className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
              Discard changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}




// ─── Infrastructure ───

function InfrastructureTab() {
  const [endpoints, setEndpoints] = useState([]);
  const [models, setModels]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [copied, setCopied]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [e, m] = await Promise.all([inferenceAPI.getEndpoints(), inferenceAPI.getModels()]);
        setEndpoints(e.data || []);
        setModels((m.data?.models || []).filter(x => x.provider === 'ollama' && x.is_active !== false));
      } catch {
        // the script still renders with placeholders
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const modelList = models.map(m => m.model_tag).join(' ') || 'qwen3.5:9b';
  const activeEndpoint = endpoints.find(e => e.provider === 'ollama' && e.is_active);

  const script = `#!/bin/bash

# Fill these two before use.
CF_TUNNEL_TOKEN="PASTE_CLOUDFLARE_TUNNEL_TOKEN"
OLLAMA_BEARER="PASTE_ENDPOINT_AUTH_TOKEN"

MODELS="${modelList}"

exec > >(tee -a /workspace/okebourne-start.log) 2>&1
echo "=== start $(date -u) ==="

export DEBIAN_FRONTEND=noninteractive
export OLLAMA_HOST=127.0.0.1:11434
export OLLAMA_KEEP_ALIVE=-1
export OLLAMA_MODELS=/workspace/.ollama
export OLLAMA_NUM_PARALLEL=2

command -v ollama >/dev/null 2>&1 || curl -fsSL https://ollama.com/install.sh | sh
pgrep -x ollama >/dev/null || nohup ollama serve >/workspace/ollama.log 2>&1 &

for i in $(seq 1 30); do
    curl -sf http://127.0.0.1:11434/api/tags >/dev/null && break
    sleep 2
done

for m in $MODELS; do
    ollama list | grep -q "^\${m}" || ollama pull "$m" || echo "WARN: pull failed for $m"
done

if ! command -v caddy >/dev/null 2>&1; then
    apt-get update -qq
    apt-get install -y -qq debian-keyring debian-archive-keyring apt-transport-https curl gpg
    curl -1sLf 'https://dl.cloudsmith.io/public/caddy/stable/gpg.key' \\
        | gpg --dearmor --yes -o /usr/share/keyrings/caddy-stable-archive-keyring.gpg
    curl -1sLf 'https://dl.cloudsmith.io/public/caddy/stable/debian.deb.txt' \\
        > /etc/apt/sources.list.d/caddy-stable.list
    apt-get update -qq
    apt-get install -y -qq caddy
fi

cat > /etc/caddy/Caddyfile <<EOF
{
	admin off
	auto_https off
}

:11435 {
	log {
		output file /workspace/caddy-access.log
		format json
	}

	@unauthorised not header Authorization "Bearer \${OLLAMA_BEARER}"
	respond @unauthorised 401

	reverse_proxy 127.0.0.1:11434 {
		header_up Host 127.0.0.1:11434
		transport http {
			read_timeout 600s
			write_timeout 600s
		}
	}
}
EOF

pkill -x caddy || true
sleep 1
nohup caddy run --config /etc/caddy/Caddyfile >/workspace/caddy.log 2>&1 &

if ! command -v cloudflared >/dev/null 2>&1; then
    mkdir -p --mode=0755 /usr/share/keyrings
    curl -fsSL https://pkg.cloudflare.com/cloudflare-public-v2.gpg \\
        > /usr/share/keyrings/cloudflare-public-v2.gpg
    echo 'deb [signed-by=/usr/share/keyrings/cloudflare-public-v2.gpg] https://pkg.cloudflare.com/cloudflared any main' \\
        > /etc/apt/sources.list.d/cloudflared.list
    apt-get update -qq
    apt-get install -y -qq cloudflared
fi

pkill -x cloudflared || true
sleep 1
nohup cloudflared tunnel --no-autoupdate run --token "$CF_TUNNEL_TOKEN" \\
    >/workspace/cloudflared.log 2>&1 &

sleep 8
echo "--- local check ---"
curl -s -o /dev/null -w "no auth (want 401): %{http_code}\\n" http://127.0.0.1:11435/api/tags
curl -s -o /dev/null -w "with auth (want 200): %{http_code}\\n" \\
    -H "Authorization: Bearer \${OLLAMA_BEARER}" http://127.0.0.1:11435/api/tags
ollama list
echo "=== ready $(date -u) ==="`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      toast.success('Script copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the text manually');
    }
  };

  return (
    <div>
      <p className="text-sm text-slate-500 max-w-3xl mb-4">
        Provisioning script for a GPU. 
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
        <div className="space-y-1.5 text-sm">
          <div className="flex gap-2">
            <span className="text-slate-400 w-32 shrink-0">Models to pull</span>
            <span className="font-mono text-slate-900 text-xs">{modelList}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-slate-400 w-32 shrink-0">Active endpoint</span>
            <span className="font-mono text-slate-900 text-xs">
              {activeEndpoint ? activeEndpoint.base_url : 'none configured'}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100">

        </p>
      </div>

      <div className="flex justify-end mb-3">
        <button onClick={handleCopy} disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? 'Copied' : 'Copy script'}
        </button>
      </div>

      <pre className="w-full max-h-[520px] overflow-auto bg-slate-900 text-slate-100 rounded-xl p-4 text-xs font-mono leading-relaxed">
        {script}
      </pre>
    </div>
  );
}


// ─── Main Page ───

export default function InferencePage() {
  const { user } = useAuth();
  const [parent, setParent] = useState('endpoints');
  const [sub, setSub]       = useState('all');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    inferenceAPI.getStatus().then(r => setStatus(r.data)).catch(() => {});
  }, [parent]);

  if (user?.role !== 'admin') {
    return <div className="text-center py-16"><p className="text-slate-400">You do not have access to AI Inference.</p></div>;
  }

  const changeParent = (id) => {
    setParent(id);
    setSub(SUBTABS[id][0] || 'all');
  };

  const subtabs = SUBTABS[parent] || [];

  return (
    <div data-testid="inference-page">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Inference</h1>
          <p className="text-slate-500 text-sm mt-1">
            Endpoints, models, prompts and compute for automated story generation
          </p>
        </div>
        {status && !status.encryption_ready && (
          <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg border border-red-200">
            <AlertTriangle size={13} /> Encryption key not set
          </span>
        )}
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 mb-5 w-fit">
        {PARENT_TABS.map(t => (
          <button key={t.id} onClick={() => changeParent(t.id)} data-testid={`inference-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              parent === t.id
                ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-900'
            }`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {subtabs.length > 0 && (
        <div className="flex gap-1 flex-wrap border-b border-slate-200 mb-5">
          {subtabs.map(s => (
            <button key={s} onClick={() => setSub(s)} data-testid={`inference-subtab-${s}`}
              className={`px-2.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                sub === s
                  ? 'border-blue-600 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
              }`}>
              {SUBTAB_LABELS[s]}
            </button>
          ))}
        </div>
      )}

      {parent === 'endpoints'      && <EndpointsTab key={sub} filter={sub} />}
      {parent === 'models'         && <ModelsTab key={sub} filter={sub} />}
      {parent === 'compute'        && <ComputeTab key={sub} view={sub} />}
      {parent === 'prompts' && sub === 'image' && <ImagePromptTab />}
      {parent === 'prompts' && sub !== 'image' && <PromptsTab key={sub} layer={sub} />}
      {parent === 'infrastructure' && <InfrastructureTab />}
    </div>
  );
}